import { ChangeNotifier } from "subtree.js/core";
import { preferWideCamera } from "../primitives/wire-stats";
import { coverCrop, type Rect, type Size, shotFromVideo } from "./scan-image";

export type CameraPhase = "off" | "starting" | "on" | "failed";

/** The camera calls a browser offers: `navigator.mediaDevices` in the app, a fake in tests. */
export type CameraDevices = Pick<MediaDevices, "getUserMedia" | "enumerateDevices">;

type WakeLock = { release?: () => Promise<void> };

const FRAME: MediaTrackConstraints = { width: { ideal: 1280 }, height: { ideal: 720 } };
const REAR: MediaStreamConstraints = { video: { facingMode: { ideal: "environment" }, ...FRAME }, audio: false };

const videoTrack = (stream: MediaStream | null) => stream?.getVideoTracks()[0] ?? null;

/** How long a press made as the camera starts waits for its first frame. */
export const FIRST_FRAME_MS = 2000;

/** Resolves once the video has a frame to draw, or after `FIRST_FRAME_MS`. */
function firstFrame(video: HTMLVideoElement): Promise<void> {
  return new Promise((resolve) => {
    const done = () => {
      clearTimeout(timer);
      video.removeEventListener("loadeddata", done);
      resolve();
    };
    const timer = setTimeout(done, FIRST_FRAME_MS);
    video.addEventListener("loadeddata", done);
  });
}

function stopAll(stream: MediaStream): void {
  for (const track of stream.getTracks()) track.stop();
}

/**
 * The scanner's live camera, opened as iOS opens it: a rear camera asked for 1280×720, the widest lens the browser
 * names (it focuses on a card held close), the torch where the camera has one, and the screen kept awake. A camera
 * the system took while the page was hidden starts again when the page shows.
 */
export class CardCamera extends ChangeNotifier {
  readonly #devices: () => CameraDevices | null;
  readonly #secure: () => boolean;
  #phase: CameraPhase = "off";
  #failure: CameraFailure | null = null;
  #stream: MediaStream | null = null;
  #video: HTMLVideoElement | null = null;
  /** Null when the camera has no torch. */
  #torch: boolean | null = null;
  #wakeLock: WakeLock | null = null;
  #run = 0;

  constructor(o: { devices: () => CameraDevices | null; secure: () => boolean }) {
    super();
    this.#devices = o.devices;
    this.#secure = o.secure;
  }

  get phase(): CameraPhase {
    return this.#phase;
  }

  get failure(): CameraFailure | null {
    return this.#failure;
  }

  get torch(): boolean | null {
    return this.#torch;
  }

  /** The preview the stream shows in, or null once it goes. */
  attach(video: HTMLVideoElement | null): void {
    this.#video = video;
    this.#show();
  }

  async start(): Promise<void> {
    if (this.#phase === "starting" || this.#phase === "on") return;
    const run = ++this.#run;
    const devices = this.#devices();
    if (devices === null) {
      this.#set("failed", this.#secure() ? "unsupported" : "insecure");
      return;
    }
    this.#set("starting", null);
    try {
      let stream = await devices.getUserMedia(REAR);
      if (run !== this.#run) return stopAll(stream);
      // Lenses are named once the camera is allowed, so the wider one is looked for now.
      const cameras = (await devices.enumerateDevices()).filter((d) => d.kind === "videoinput");
      const wide = preferWideCamera(cameras, videoTrack(stream)?.getSettings().deviceId ?? "");
      if (run !== this.#run) return stopAll(stream);
      if (wide !== null) {
        stopAll(stream);
        stream = await devices
          .getUserMedia({ video: { deviceId: { exact: wide }, ...FRAME }, audio: false })
          .catch(() => devices.getUserMedia(REAR));
        if (run !== this.#run) return stopAll(stream);
      }
      this.#stream = stream;
      const capabilities = videoTrack(stream)?.getCapabilities?.() as { torch?: boolean } | undefined;
      this.#torch = capabilities?.torch === true ? false : null;
      this.#show();
      this.#listen(true);
      await this.#keepAwake(run);
      if (run !== this.#run) return;
      this.#set("on", null);
    } catch (e) {
      if (run !== this.#run) return;
      this.#release();
      this.#set("failed", cameraFailureOf(e));
    }
  }

  stop(): void {
    ++this.#run;
    this.#listen(false);
    this.#release();
    this.#set("off", null);
  }

  async setTorch(on: boolean): Promise<void> {
    const track = videoTrack(this.#stream);
    if (track === null || this.#torch === null) return;
    try {
      await track.applyConstraints({ advanced: [{ torch: on } as MediaTrackConstraintSet] });
      this.#torch = on;
    } catch {
      // The light stays as it was.
    }
    this.notifyListeners();
  }

  /** The guide's crop of the frame showing now, as a scan sends it; null when none shows. */
  async shot(view: Size, guide: Rect): Promise<Blob | null> {
    const video = this.#video;
    if (video === null || this.#phase !== "on") return null;
    // The camera is on before its first frame reaches the preview: a press in between waits for it.
    if (video.videoWidth === 0) {
      const run = this.#run;
      await firstFrame(video);
      if (run !== this.#run || this.#video !== video) return null;
    }
    const crop = coverCrop(view, { width: video.videoWidth, height: video.videoHeight }, guide);
    return crop === null ? null : shotFromVideo(video, crop);
  }

  override dispose(): void {
    this.stop();
    super.dispose();
  }

  #show(): void {
    const video = this.#video;
    if (video === null || video.srcObject === this.#stream) return;
    video.srcObject = this.#stream;
    if (this.#stream !== null) void video.play().catch(() => undefined);
  }

  #release(): void {
    if (this.#stream !== null) stopAll(this.#stream);
    this.#stream = null;
    this.#torch = null;
    this.#show();
    void this.#wakeLock?.release?.().catch(() => undefined);
    this.#wakeLock = null;
  }

  #set(phase: CameraPhase, failure: CameraFailure | null): void {
    this.#phase = phase;
    this.#failure = failure;
    this.notifyListeners();
  }

  /** Keeps the screen on while the camera runs; a lock granted after the camera stopped is let go at once. */
  async #keepAwake(run: number): Promise<void> {
    try {
      const nav =
        typeof navigator === "undefined"
          ? undefined
          : (navigator as { wakeLock?: { request(type: "screen"): Promise<WakeLock> } });
      if (!nav?.wakeLock) return;
      const lock = await nav.wakeLock.request("screen");
      if (run === this.#run) this.#wakeLock = lock;
      else void lock.release?.().catch(() => undefined);
    } catch {
      // The screen may lock; the camera goes on.
    }
  }

  #listen(on: boolean): void {
    if (typeof document === "undefined") return;
    document.removeEventListener("visibilitychange", this.#onVisibility);
    if (on) document.addEventListener("visibilitychange", this.#onVisibility);
  }

  readonly #onVisibility = (): void => {
    if (document.visibilityState !== "visible" || this.#phase !== "on") return;
    if (videoTrack(this.#stream)?.readyState === "live") {
      void this.#keepAwake(this.#run);
      return;
    }
    this.stop();
    void this.start();
  };
}

/** Why the live camera is not showing. */
export type CameraFailure = "denied" | "none" | "busy" | "insecure" | "unsupported" | "onAir" | "other";

/** A camera error, by the name the browser gives it. */
export function cameraFailureOf(error: unknown): CameraFailure {
  const name = typeof error === "object" && error !== null && "name" in error ? String(error.name) : "";
  if (name === "NotAllowedError" || name === "SecurityError") return "denied";
  if (name === "NotFoundError" || name === "OverconstrainedError") return "none";
  if (name === "NotReadableError" || name === "AbortError") return "busy";
  return "other";
}
